import { Download, Eye, FileText, Pencil, Plus, Stethoscope, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API, api, errMsg } from "@/lib/api";

import { ProfessionalsTab } from "./ProfessionalsTab";
import { RevenueTab } from "./RevenueTab";

const EMPTY_ITEM = {
  category: "",
  tipo: "otro",
  name: "",
  active_ingredient: "",
  presentation: "",
  dose: "",
  route: "",
  notes: "",
};

function CatalogTable({ items, onEdit, onDelete }) {
  return (
    <div className="border rounded-2xl bg-white overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>Principio activo</TableHead>
            <TableHead>Presentación</TableHead>
            <TableHead>Dosis</TableHead>
            <TableHead>Vía</TableHead>
            <TableHead className="w-24"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((i) => (
            <TableRow key={i.id} data-testid={`catalog-row-${i.id}`}>
              <TableCell className="font-semibold">{i.name}</TableCell>
              <TableCell className="text-muted-foreground">{i.active_ingredient}</TableCell>
              <TableCell className="text-muted-foreground">{i.presentation}</TableCell>
              <TableCell className="text-muted-foreground max-w-[220px]">{i.dose}</TableCell>
              <TableCell className="text-muted-foreground">{i.route}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <button className="p-1.5 hover:text-primary" onClick={() => onEdit(i)} data-testid={`catalog-edit-${i.id}`}>
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                  <button className="p-1.5 hover:text-destructive" onClick={() => onDelete(i.id)} data-testid={`catalog-delete-${i.id}`}>
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const CATALOG_FORM_FIELDS = [
  ["category", "Categoría", "Ej. Estradioles puros"],
  ["name", "Nombre comercial", ""],
  ["active_ingredient", "Principio activo", ""],
  ["presentation", "Presentación", ""],
  ["dose", "Dosis", ""],
  ["route", "Vía", ""],
  ["notes", "Notas", ""],
];

function CatalogFormDialog({ editing, setEditing, onSave }) {
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent className="rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing?.isNew ? "Agregar opción THAG" : "Editar opción THAG"}</DialogTitle>
        </DialogHeader>
        {editing && (
          <div className="grid grid-cols-2 gap-4">
            {CATALOG_FORM_FIELDS.map(([key, label, ph]) => (
              <div key={key} className={`space-y-1.5 ${["category", "dose"].includes(key) ? "col-span-2" : ""}`}>
                <Label>{label}</Label>
                <Input
                  value={editing.item[key] || ""}
                  placeholder={ph}
                  onChange={(e) => setEditing({ ...editing, item: { ...editing.item, [key]: e.target.value } })}
                  data-testid={`catalog-form-${key}`}
                />
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button className="rounded-full" onClick={onSave} data-testid="catalog-save-btn">
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | {item, isNew}
  const load = () => api.get("/catalog").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const { item, isNew } = editing;
    try {
      if (isNew) {
        await api.post("/catalog", item);
      } else {
        await api.put(`/catalog/${item.id}`, item);
      }
      toast.success("Catálogo actualizado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/catalog/${id}`);
      toast.success("Elemento eliminado");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const categories = [...new Set(items.map((i) => i.category))];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button className="rounded-full" onClick={() => setEditing({ item: { ...EMPTY_ITEM }, isNew: true })} data-testid="catalog-add-btn">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Agregar opción
        </Button>
      </div>
      {categories.map((cat) => (
        <div key={cat} className="mb-8">
          <h3 className="font-heading font-semibold mb-3">{cat}</h3>
          <CatalogTable
            items={items.filter((i) => i.category === cat)}
            onEdit={(item) => setEditing({ item: { ...item }, isNew: false })}
            onDelete={remove}
          />
        </div>
      ))}
      <CatalogFormDialog editing={editing} setEditing={setEditing} onSave={save} />
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  const ROLE = { patient: "Paciente", professional: "Profesional", admin: "Admin" };
  return (
    <div className="border rounded-2xl bg-white overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>Correo</TableHead>
            <TableHead>Rol</TableHead>
            <TableHead>Registro</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.user_id}>
              <TableCell className="font-semibold">{u.name}</TableCell>
              <TableCell className="text-muted-foreground">{u.email}</TableCell>
              <TableCell>{ROLE[u.role] || "Sin rol"}</TableCell>
              <TableCell className="text-muted-foreground">
                {u.created_at ? new Date(u.created_at).toLocaleDateString("es-MX") : ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RequestsTab() {
  const [reqs, setReqs] = useState([]);
  useEffect(() => {
    api.get("/admin/requests").then((r) => setReqs(r.data)).catch(() => {});
  }, []);

  const exportCSV = async () => {
    try {
      const r = await api.get("/admin/requests/export", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solicitudes_recetrans_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">
          {reqs.length} solicitud{reqs.length === 1 ? "" : "es"} en total
        </p>
        <Button className="rounded-full" onClick={exportCSV} disabled={reqs.length === 0} data-testid="admin-export-csv-btn">
          <Download className="h-4 w-4 mr-2" strokeWidth={1.5} /> Descargar CSV
        </Button>
      </div>
      <div className="border rounded-2xl bg-white overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Paciente</TableHead>
            <TableHead>Profesional</TableHead>
            <TableHead>Folio</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Documentos subidos</TableHead>
            <TableHead>Receta final</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reqs.map((r) => (
            <TableRow key={r.id} data-testid={`admin-request-row-${r.id}`}>
              <TableCell className="font-semibold">
                {r.personal?.nombres} {r.personal?.apellidos}
              </TableCell>
              <TableCell className="text-muted-foreground">{r.professional_name}</TableCell>
              <TableCell>{r.folio || "—"}</TableCell>
              <TableCell>
                <StatusBadge status={r.status} />
              </TableCell>
              <TableCell>
                {r.files?.length ? (
                  <div className="flex flex-col gap-1">
                    {r.files.map((f) => (
                      <a
                        key={f.file_id}
                        href={`${API}/files/${f.file_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        data-testid={`admin-file-link-${f.file_id}`}
                      >
                        <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
                        {{ lab: "Lab", receta_anterior: "Receta previa", payment_proof: "Comprobante" }[f.kind] || "Doc"} · {f.original_filename.slice(0, 24)}
                      </a>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell>
                {r.status === "completed" ? (
                  <a
                    href={`${API}/requests/${r.id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    data-testid={`admin-pdf-link-${r.id}`}
                  >
                    <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> Ver PDF
                  </a>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(r.created_at).toLocaleDateString("es-MX")}
              </TableCell>
              <TableCell>
                <Link
                  to={`/pro/solicitud/${r.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border hover:bg-secondary transition-colors duration-200"
                  data-testid={`admin-open-request-${r.id}`}
                >
                  <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> Abrir
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-dashboard">
        <p className="text-sm text-muted-foreground uppercase tracking-widest mb-1">Administración</p>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold">Panel de TransSalud</h1>
          <Link to="/dashboard" data-testid="goto-pro-panel-btn">
            <Button variant="outline" className="rounded-full">
              <Stethoscope className="h-4 w-4 mr-2" strokeWidth={1.5} /> Mi panel de pacientes
            </Button>
          </Link>
        </div>
        <Tabs defaultValue="profesionales">
          <TabsList className="rounded-full mb-8 flex-wrap h-auto">
            <TabsTrigger value="profesionales" className="rounded-full" data-testid="admin-tab-profesionales">
              Profesionales
            </TabsTrigger>
            <TabsTrigger value="catalogo" className="rounded-full" data-testid="admin-tab-catalogo">
              Catálogo THAG
            </TabsTrigger>
            <TabsTrigger value="usuarios" className="rounded-full" data-testid="admin-tab-usuarios">
              Usuarios
            </TabsTrigger>
            <TabsTrigger value="solicitudes" className="rounded-full" data-testid="admin-tab-solicitudes">
              Solicitudes
            </TabsTrigger>
            <TabsTrigger value="ingresos" className="rounded-full" data-testid="admin-tab-ingresos">
              Ingresos
            </TabsTrigger>
          </TabsList>
          <TabsContent value="profesionales">
            <ProfessionalsTab />
          </TabsContent>
          <TabsContent value="catalogo">
            <CatalogTab />
          </TabsContent>
          <TabsContent value="usuarios">
            <UsersTab />
          </TabsContent>
          <TabsContent value="solicitudes">
            <RequestsTab />
          </TabsContent>
          <TabsContent value="ingresos">
            <RevenueTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
